<script>
import { mapGetters } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { getSidebarItems } from './config/default-sidebar';
import { reportsEntryRouteName } from './config/sidebarItems/primaryMenu';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useRoute, useRouter } from 'dashboard/composables/route';
import { REDIS_FEATURE_FLAGS } from 'dashboard/featureFlags';

const COMPANIES_FEATURE_FLAG = 'companies';
import { canShowCloudchatSupportWidget } from 'dashboard/helper/cloudchatSupportHelper';

import PrimarySidebar from './sidebarComponents/Primary.vue';
import SecondarySidebar from './sidebarComponents/Secondary.vue';
import { routesWithPermissions } from '../../routes';
import {
  getUserPermissions,
  hasPermissions,
} from '../../helper/permissionsHelper';
import { isClaudiaAuditableInbox } from 'shared/mixins/inboxMixin';

export default {
  components: {
    PrimarySidebar,
    SecondarySidebar,
  },
  props: {
    showSecondarySidebar: {
      type: Boolean,
      default: true,
    },
    sidebarClassName: {
      type: String,
      default: '',
    },
  },
  setup(props, { emit }) {
    const route = useRoute();
    const router = useRouter();

    const toggleKeyShortcutModal = () => {
      emit('openKeyShortcutModal');
    };
    const closeKeyShortcutModal = () => {
      emit('closeKeyShortcutModal');
    };
    const isCurrentRouteSameAsNavigation = routeName => {
      return route.name === routeName;
    };
    const navigateToRoute = routeName => {
      if (!isCurrentRouteSameAsNavigation(routeName)) {
        router.push({ name: routeName });
      }
    };
    const isFeatureEnabledonAccount = useMapGetter(
      'accounts/isFeatureEnabledonAccount'
    );
    const currentAccountId = useMapGetter('getCurrentAccountId');
    const currentRole = useMapGetter('getCurrentRole');
    // Evaluated at keypress time (not once in setup) so switching accounts mid
    // session can't leave the shortcuts bound to a stale claudia-only state.
    const isClaudiaOnly = () =>
      isFeatureEnabledonAccount.value(
        currentAccountId.value,
        REDIS_FEATURE_FLAGS.CLAUDIA_ONLY
      );

    const keyboardEvents = {
      '$mod+Slash': {
        action: toggleKeyShortcutModal,
      },
      '$mod+Escape': {
        action: closeKeyShortcutModal,
      },
      'Alt+KeyC': {
        action: () => navigateToRoute('home'),
      },
      'Alt+KeyS': {
        action: () => navigateToRoute('agent_list'),
      },
      // Contacts and Reports are hidden in claudia-only accounts; guard at
      // keypress so the binding follows the current account, not setup state.
      'Alt+KeyV': {
        action: () => {
          if (isClaudiaOnly()) return;
          navigateToRoute('contacts_dashboard');
        },
      },
      'Alt+KeyR': {
        action: () => {
          if (isClaudiaOnly()) return;
          navigateToRoute(reportsEntryRouteName(currentRole.value));
        },
      },
    };
    useKeyboardEvents(keyboardEvents);

    return {
      toggleKeyShortcutModal,
    };
  },
  data() {
    return {
      showOptionsMenu: false,
    };
  },

  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
      inboxes: 'inboxes/getInboxes',
      isACustomBrandedInstance: 'globalConfig/isACustomBrandedInstance',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isHubAdmin: 'hub/getIsHubAdmin',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
      labels: 'labels/getLabelsOnSidebar',
      teams: 'teams/getMyTeams',
      getAccount: 'accounts/getAccount',
      currentRole: 'getCurrentRole',
      isNewFeatureFlagEnabled: 'isNewFeatureFlagEnabled',
    }),
    activeCustomView() {
      if (this.activePrimaryMenu.key === 'contacts') {
        return 'contact';
      }
      if (this.activePrimaryMenu.key === 'conversations') {
        return 'conversation';
      }
      return '';
    },
    isClaudiaOnly() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        REDIS_FEATURE_FLAGS.CLAUDIA_ONLY
      );
    },
    canShowCloudchatSupportWidget() {
      return canShowCloudchatSupportWidget({
        accountId: this.accountId,
        role: this.currentRole,
      });
    },
    isOmniServiceTimeMetricsReportEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        REDIS_FEATURE_FLAGS.OMNI_SERVICE_TIME_METRICS_REPORT
      );
    },
    isOmniCampaignMetricsReportEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        REDIS_FEATURE_FLAGS.OMNI_CAMPAIGN_METRICS_REPORT
      );
    },
    isOmniCsatReportEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        REDIS_FEATURE_FLAGS.OMNI_CSAT_REPORT
      );
    },
    isOmniConversationsReportEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        REDIS_FEATURE_FLAGS.OMNI_CONVERSATIONS_REPORT
      );
    },
    isOmniAgentsReportEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        REDIS_FEATURE_FLAGS.OMNI_AGENTS_REPORT
      );
    },
    isOmniTeamsReportEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        REDIS_FEATURE_FLAGS.OMNI_TEAMS_REPORT
      );
    },
    isParallelConversationsEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        REDIS_FEATURE_FLAGS.PARALLEL_CONVERSATIONS
      );
    },
    customViews() {
      return this.$store.getters['customViews/getCustomViewsByFilterType'](
        this.activeCustomView
      );
    },
    isConversationOrContactActive() {
      return (
        this.activePrimaryMenu.key === 'contacts' ||
        this.activePrimaryMenu.key === 'conversations'
      );
    },
    sideMenuConfig() {
      const account = this.getAccount(this.accountId);
      const accountLocale = account?.locale || 'pt_BR';
      return getSidebarItems(
        this.accountId,
        accountLocale,
        this.isClaudiaOnly,
        {
          omniServiceTimeMetricsReport:
            this.isOmniServiceTimeMetricsReportEnabled,
          omniCampaignMetricsReport: this.isOmniCampaignMetricsReportEnabled,
          omniCsatReport: this.isOmniCsatReportEnabled,
          omniConversationsReport: this.isOmniConversationsReportEnabled,
          omniAgentsReport: this.isOmniAgentsReportEnabled,
          omniTeamsReport: this.isOmniTeamsReportEnabled,
          parallelConversations: this.isParallelConversationsEnabled,
          companies: this.isNewFeatureFlagEnabled(COMPANIES_FEATURE_FLAG),
        },
        {
          isHubAdmin: this.isHubAdmin,
          role: this.currentRole,
          hasClaudiaInboxes: this.hasClaudiaInboxes,
        }
      );
    },
    // Whether any inbox is answered by ClaudIA — either bound to a ClaudIA
    // project or a mirror inbox. Gates the audit view, whose preset is scoped
    // to exactly those inboxes (see `claudiaAuditInboxes` in ChatList.vue).
    hasClaudiaInboxes() {
      return (this.inboxes || []).some(inbox => isClaudiaAuditableInbox(inbox));
    },
    primaryMenuItems() {
      const userPermissions = getUserPermissions(
        this.currentUser,
        this.accountId
      );
      const menuItems = this.sideMenuConfig.primaryMenu;
      return menuItems.filter(menuItem => {
        if (menuItem.openInNewPage) {
          return true;
        }

        const isAvailableForTheUser = hasPermissions(
          routesWithPermissions[menuItem.toStateName],
          userPermissions
        );

        if (!isAvailableForTheUser) {
          return false;
        }
        if (
          menuItem.alwaysVisibleOnChatwootInstances &&
          !this.isACustomBrandedInstance
        ) {
          return true;
        }
        if (menuItem.featureFlag) {
          return this.isFeatureEnabledonAccount(
            this.accountId,
            menuItem.featureFlag
          );
        }
        if (menuItem.redisFeatureFlag) {
          return this.isFeatureEnabledonAccount(
            this.accountId,
            menuItem.redisFeatureFlag
          );
        }
        if (menuItem.superAdminOnly)
          return this.currentUser?.type === 'SuperAdmin';
        return true;
      });
    },
    activeSecondaryMenu() {
      const { secondaryMenu } = this.sideMenuConfig;
      const { name: currentRoute } = this.$route;

      const activeSecondaryMenu =
        secondaryMenu.find(menuItem =>
          menuItem.routes.includes(currentRoute)
        ) || {};
      return activeSecondaryMenu;
    },
    activePrimaryMenu() {
      const activePrimaryMenu =
        this.primaryMenuItems.find(
          menuItem => menuItem.key === this.activeSecondaryMenu.parentNav
        ) || {};
      return activePrimaryMenu;
    },
  },

  watch: {
    activeCustomView() {
      this.fetchCustomViews();
    },
  },
  mounted() {
    this.$store.dispatch('labels/get');
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('notifications/unReadCount');
    this.$store.dispatch('teams/get');
    this.$store.dispatch('attributes/get');
    // Which custom attributes THIS user may not see. One request per session (the
    // map is per account, not per conversation), and it never rejects — a failure
    // leaves the list unfiltered, exactly as before the feature existed.
    this.$store.dispatch('customAttributePermissions/get');
    this.fetchCustomViews();
    if (this.isClaudiaOnly) {
      this.$store.dispatch('hub/fetchTenant');
    }
  },

  methods: {
    fetchCustomViews() {
      if (this.isConversationOrContactActive) {
        this.$store.dispatch('customViews/get', this.activeCustomView);
      }
    },
    toggleSupportChatWindow() {
      window.$cloudchat.toggle();
    },
    toggleAccountModal() {
      this.$emit('toggleAccountModal');
    },
    showAddCustomView() {
      this.$emit('showAddCustomView');
    },
    showAddLabelPopup() {
      this.$emit('showAddLabelPopup');
    },
    openNotificationPanel() {
      this.$emit('openNotificationPanel');
    },
  },
};
</script>

<template>
  <aside class="flex h-full">
    <PrimarySidebar
      :logo-source="globalConfig.logoThumbnail"
      :installation-name="globalConfig.installationName"
      :is-a-custom-branded-instance="isACustomBrandedInstance"
      :account-id="accountId"
      :menu-items="primaryMenuItems"
      :active-menu-item="activePrimaryMenu.key"
      :claudia-only="isClaudiaOnly"
      :show-support-launcher="canShowCloudchatSupportWidget"
      @toggleAccounts="toggleAccountModal"
      @openKeyShortcutModal="toggleKeyShortcutModal"
      @openNotificationPanel="openNotificationPanel"
    />
    <SecondarySidebar
      v-if="showSecondarySidebar"
      :class="sidebarClassName"
      :account-id="accountId"
      :inboxes="inboxes"
      :labels="labels"
      :teams="teams"
      :custom-views="customViews"
      :menu-config="activeSecondaryMenu"
      :current-user="currentUser"
      :is-on-chatwoot-cloud="isOnChatwootCloud"
      @addLabel="showAddLabelPopup"
      @addCustomView="showAddCustomView"
      @toggleAccounts="toggleAccountModal"
    />
  </aside>
</template>
